import React, { useState, useEffect, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { fetchEomTable, EomTableRow, peekCache } from '../services/api';
import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23
import DataTable from '../components/DataTable';
import { TableSkeleton, SkeletonBox } from '../components/Skeleton';
import { LoadFailed, EmptyState } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { Column, SortConfig } from '../types';
import { layoutMax } from '../utils/appZoom'; // [APP-FIT 2026-10-26] laptop width rules follow the laid-out width

// [STICKY-HEAD 2026-06-26] Same freeze pattern as PaymentsPage: fill the scroll
// card exactly and don't scroll HERE — the KPI cards + "PM Inventory Analysis"
// heading stay fixed and only the table area (below) scrolls. (Needs AppLayout's
// PageCard $flush on the /month-on-month route so the card itself doesn't scroll.)
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: ${(p) => p.theme.surface};

  /* [PHONE-LAYOUT 2026-10-23, user] phones: NOTHING is fixed at the top — the KPI cards, the heading and the table are ONE
     scroll ("in the phone view nothing will be fixed on the top side otherwise how will i see the data for the below";
     the frozen cards + heading took about 55 % of a phone). The table only swipes sideways (TableScroll below). */
  @media (max-width: 768px) {
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

// The FIXED top block — the KPI cards + "PM Inventory Analysis" heading. Sits
// OUTSIDE the scrolling table area (flex-shrink:0) so it never scrolls.
const FrozenTop = styled.div`
  flex-shrink: 0;
  background: ${(p) => p.theme.surface};
`;

// The scrolling table region — owns the vertical scroll. The DataTable's sticky
// <thead> pins to the top of THIS area (via parentScroll), so the column header
// stays just under the frozen heading while rows scroll. padding-bottom clears the
// floating dock at the very end of the scroll so the last row never hides behind it.
const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: scroll; /* [SCROLLBAR 2026-07-08] always show the vertical scroller on desktop */
  padding-bottom: 88px;

  /* [PHONE-LAYOUT 2026-10-23, user] phones: as tall as its rows (no box of its own to scroll up / down in) — it only
     swipes sideways; the page scrolls (Container). The 88 px end pad still lifts the last row above the dock. */
  @media (max-width: 768px) {
    flex: none;
    min-height: 0;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  flex-wrap: wrap;
  gap: 10px;

  /* Phones: stack the title / filter row vertically (each full-width). */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 10px 12px;
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
`;


const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  padding: 16px 20px;
  ${layoutMax(1100)} { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  /* Phones: keep TWO KPIs per row (a 2×2 grid) with tighter spacing — the cards
     below shrink their padding/value font so the money values still fit two-up. */
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    padding: 12px;
  }
`;

const KpiCard = styled.div`
  position: relative;
  isolation: isolate;
  /* [LIQUID-GLASS 2026-11-02, user] glass: a shine on the top half and a light top edge (below) */
  background: ${(p) => p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0) 50%), linear-gradient(180deg, rgba(86, 86, 86, 0.42) 0%, rgba(54, 54, 54, 0.55) 100%)'
    : 'linear-gradient(180deg, #ffffff, rgba(255,255,255,0) 55%), rgba(248, 250, 253, 0.92)'};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(22,25,23,0.10)'};
  border-radius: 14px;
  padding: 18px 18px 16px;
  overflow: hidden;
  box-shadow:
    0 12px 32px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.10)'},
    0 4px 12px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.06)'},
    inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.9)'},
    inset 0 -1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.04)'};
  transition: transform 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1), border-color 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(0,0,0,0) 80%, rgba(0,0,0,0.10) 100%);
    pointer-events: none;
    z-index: 0;
  }
  & > * { position: relative; z-index: 1; }

  &:hover {
    transform: translateY(-1px);
    box-shadow:
      0 16px 40px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.14)'},
      0 4px 12px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.08)'},
      inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.9)'},
      inset 0 -1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.28)' : 'rgba(0,0,0,0.05)'};
  }

  /* Two-up on phones: tighter padding so a pair of cards fits the row cleanly. */
  @media (max-width: 768px) {
    padding: 13px 12px 12px;
    border-radius: 12px;
  }
`;

const KpiLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  text-align: center;
  margin-bottom: 8px;
  /* [PHONE-LAYOUT 2026-10-22] 11 px on phones (was 10 px, below the phone minimum); long labels wrap. */
  @media (max-width: 768px) { font-size: 11px; letter-spacing: 0.4px; margin-bottom: 6px; }
`;

const KpiValue = styled.div<{ $color?: string }>`
  font-size: 28px;
  font-weight: 800;
  text-align: center;
  color: ${(p) => p.$color || p.theme.text};
  margin-bottom: 6px;
  line-height: 1.1;
  /* Shrink so long money values (e.g. $188,273.38) fit a half-width phone card. */
  @media (max-width: 768px) { font-size: 18px; }
`;

const KpiSub = styled.div<{ $tone?: 'up' | 'down' | 'neutral' }>`
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  color: ${(p) => (p.$tone === 'up' ? (p.theme.colorScheme === 'dark' ? '#44b57b' : '#13b790') : p.$tone === 'down' ? '#ef4444' : p.theme.muted)};
  margin-bottom: 10px;
  @media (max-width: 768px) { font-size: 11px; margin-bottom: 8px; }
`;

const KpiDesc = styled.div`
  font-size: 11px;
  text-align: center;
  color: ${(p) => p.theme.muted};
  line-height: 1.4;
  /* [PHONE-LAYOUT 2026-10-22] 11 px on phones (was 10 px). */
  @media (max-width: 768px) { font-size: 11px; }
`;

/* Loading placeholder — reuses the REAL KpiRow + KpiCard (centred label / value /
   sub / 2-line desc) so the skeleton matches the live cards instead of the generic
   bordered <KpiSkeleton> wrap. */
const MomKpiSkeleton: React.FC = () => (
  <KpiRow aria-busy="true" aria-label="Loading">
    {Array.from({ length: 4 }).map((_, i) => (
      <KpiCard key={i}>
        <SkeletonBox $w="62%" $h="10px" $align="center" />
        <SkeletonBox $w="50%" $h="24px" $align="center" $mt="10px" />
        <SkeletonBox $w="72%" $h="10px" $align="center" $mt="12px" />
        <SkeletonBox $w="92%" $h="9px" $align="center" $mt="12px" />
        <SkeletonBox $w="78%" $h="9px" $align="center" $mt="6px" />
      </KpiCard>
    ))}
  </KpiRow>
);

const columns: Column[] = [
  { key: 'month', label: 'Month', render: (val: string) => formatMonthName(val) },
  { key: 'pm_inventory', label: 'PM Inventory', align: 'center' },
  { key: 'pm_retention_achieved', label: 'PM Retention Achieved', align: 'center' },
  { key: 'retention_pct', label: 'Retention %', align: 'center' },
  { key: 'recurring_new_sale', label: 'Recurring New Sale', align: 'center' },
  { key: 'one_time_new_sale', label: 'One Time New Sale', align: 'center' },
  { key: 'total_new_sales', label: 'Total New Sales', align: 'center' },
  { key: 'recurring_upsell', label: 'Recurring Upsell', align: 'center' },
  { key: 'one_time_upsell', label: 'One Time Upsell', align: 'center' },
  { key: 'total_pm', label: 'Total PM', align: 'center' },
  { key: 'refunds', label: 'Refunds', align: 'center' },
  { key: 'disputes_won', label: 'Disputes Won', align: 'center' },
  /* [MoM PIA/RECOVERY 2026-09-01, user] Two columns after Disputes Won, at the user's direction,
     so the money this table used to leave out is visible instead of only folded into the totals.
     ⚠ REPORTING columns, not additions — PIA is already inside Total New Sales (its Sales half)
     and Total PM (its PM half), and Recovery is already inside Disputes Won. Do not add either
     into Net SEO Total again.
     A dash, not $0, on the frozen pre-Jun-2026 rows: those came from the Google Sheet, which
     never carried this breakdown, so $0 would be a claim the data cannot support. */
  { key: 'pia', label: 'PIA', align: 'center', render: (v: string | null) => v ?? '—' },
  { key: 'recovery', label: 'Recovery', align: 'center', render: (v: string | null) => v ?? '—' },
  /* [SHOPIFY 2026-10-19, user] The EOM tab's Shopify line. Unlike PIA and Recovery it is in no other column, so Net SEO
     Total INCLUDES it — the EOM tab's own sum. A dash on the frozen sheet rows (no Shopify payout before 15 Oct 2026). */
  { key: 'shopify', label: 'Shopify', align: 'center', render: (v: string | null | undefined) => v ?? '—' },
  { key: 'net_seo_total', label: 'Net SEO Total', align: 'center' },
];

const MONTHS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function monthSortKey(m: string | null): number {
  if (!m) return 0;
  const parts = m.trim().toLowerCase().split(/\s+/);
  if (parts.length !== 2) return 0;
  const mi = MONTHS.indexOf(parts[0].slice(0, 3));
  const y = parseInt(parts[1], 10);
  if (mi < 0 || isNaN(y)) return 0;
  return y * 12 + mi;
}

// Expand the stored abbreviated month (e.g. "Jun 2026") to its full name
// ("June 2026") for display in the Month column. Also handles an ISO "2026-06"
// form just in case; falls back to the raw value if it doesn't parse.
function formatMonthName(m: string | null): string {
  if (!m) return '—';
  const s = m.trim();
  const iso = s.match(/^(\d{4})-(\d{1,2})/);
  if (iso) {
    const mi = parseInt(iso[2], 10) - 1;
    if (mi >= 0 && mi < 12) return `${MONTHS_FULL[mi]} ${iso[1]}`;
  }
  const parts = s.split(/\s+/);
  if (parts.length === 2) {
    const mi = MONTHS.indexOf(parts[0].slice(0, 3).toLowerCase());
    if (mi >= 0) return `${MONTHS_FULL[mi]} ${parts[1]}`;
  }
  return s;
}

function parseMoney(v: string | null): number {
  if (!v) return 0;
  const n = parseFloat(v.replace(/[$,\s]/g, ''));
  return isNaN(n) ? 0 : n;
}

function parsePct(v: string | null): number {
  if (!v) return 0;
  const n = parseFloat(v.replace(/[%\s]/g, ''));
  if (isNaN(n)) return 0;
  return n > 0 && n <= 1 ? n * 100 : n;
}

function formatMoney(n: number): string {
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

interface KpiData {
  potential: number;
  latestInv: number;
  latestMonth: string;
  avgRetRate: number;
  avgInv: number;
  monthCount: number;
  latestRetPct: number;
  rolling3: number | null;
  prev3: number | null;
}

function computeKpis(rows: EomTableRow[]): KpiData | null {
  if (!rows || rows.length < 1) return null;
  // Sort newest → oldest
  const sorted = [...rows].sort((a, b) => monthSortKey(b.month) - monthSortKey(a.month));
  const inventory = sorted.map(r => parseMoney(r.pm_inventory));
  const retPct = sorted.map(r => parsePct(r.retention_pct));

  const avgRetRate = retPct.reduce((s, v) => s + v, 0) / retPct.length;
  const latestInv = inventory[0];
  const latestMonth = sorted[0].month ?? '';
  const potential = latestInv * (avgRetRate / 100);
  const avgInv = inventory.reduce((s, v) => s + v, 0) / inventory.length;
  const latestRetPct = retPct[0];

  const rolling3 = retPct.length >= 3 ? (retPct[0] + retPct[1] + retPct[2]) / 3 : null;
  const prev3 = retPct.length >= 6 ? (retPct[3] + retPct[4] + retPct[5]) / 3 : null;

  return { potential, latestInv, latestMonth, avgRetRate, avgInv, monthCount: sorted.length, latestRetPct, rolling3, prev3 };
}

/**
 * The "Month on Month" page (that's the nav label users see; section key `eom`).
 * It renders the EOM table via /api/eom-table (server `eomCache`), now sourced
 * entirely from the MySQL DB — settled months (<= May 2026) from the frozen
 * `eom_snapshot`, and June 2026+ computed live from inventory/payments/refunds/
 * disputes. Google Sheets is no longer read anywhere in the app.
 *
 * Do not confuse with the separate nav button labeled "EOM" → `EOMPage`
 * (section key `dashboard`), which is a different DB-backed end-of-month dashboard.
 */
const MonthOnMonthPage: React.FC = () => {
  // [GRAPHITE-EMERALD 2026-09-28] "up" green: jade in light, emerald in dark.
  const scTheme = useTheme();
  const upColor = scTheme.colorScheme === 'dark' ? '#44b57b' : '#13b790';
  // [TAB-FAST 2026-07-07] Seed from the SWR GET cache (idle-prefetch/prior-visit warmed) so a
  // warmed revisit paints rows immediately with no skeleton; a cold visit still shows it. The
  // mount effect below still fetches and revalidates. A miss is a safe no-op (empty + skeleton).
  const [rows, setRows] = useState<EomTableRow[]>(() => peekCache<{ rows: EomTableRow[] }>('/eom-table')?.rows ?? []);
  const [loading, setLoading] = useState<boolean>(() => !peekCache<{ rows: EomTableRow[] }>('/eom-table'));
  const [sort, setSort] = useState<SortConfig>({ sortBy: 'month', sortOrder: 'desc' });
  // [LOAD-STATES 2026-09-29, user] The first load failed: countdown card, not "No EOM data found".
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchEomTable()
      .then((res) => { if (alive) { setRows(res.rows); setLoadFailed(false); } })
      .catch((err) => { console.error('Failed to load EOM table:', err); if (alive) setLoadFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // [REALTIME] 2026-06-23 — silently refresh the month-on-month table when the
  // server signals the EOM data changed (from any source).
  // [LOAD-STATES 2026-09-29] Returned as a promise so the countdown card can use it as its retry.
  const reload = () => fetchEomTable().then((res) => { setRows(res.rows); setLoadFailed(false); }).catch(() => {});
  useRealtime(['eom'], () => { void reload(); });

  const kpis = useMemo(() => computeKpis(rows), [rows]);

  const sorted = useMemo(() => {
    if (!sort.sortBy) return rows;
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    const k = sort.sortBy as keyof EomTableRow;
    return [...rows].sort((a, b) => {
      if (k === 'month') return (monthSortKey(a.month) - monthSortKey(b.month)) * dir;
      const av = (a[k] ?? '').toString();
      const bv = (b[k] ?? '').toString();
      const an = parseFloat(av.replace(/[$,%\s]/g, ''));
      const bn = parseFloat(bv.replace(/[$,%\s]/g, ''));
      if (!isNaN(an) && !isNaN(bn)) return (an - bn) * dir;
      return av.localeCompare(bv) * dir;
    });
  }, [rows, sort]);

  const handleSort = (key: string) => {
    setSort((prev) => prev.sortBy === key
      ? { sortBy: key, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' }
      : { sortBy: key, sortOrder: 'asc' });
  };

  const renderKpis = () => {
    if (!kpis) return null;
    const { potential, latestInv, latestMonth, avgRetRate, avgInv, monthCount, latestRetPct, rolling3, prev3 } = kpis;

    const latestDiff = latestRetPct - avgRetRate;
    const latestTone: 'up' | 'down' | 'neutral' = latestDiff > 0 ? 'up' : latestDiff < 0 ? 'down' : 'neutral';
    const latestColor = latestDiff > 0 ? upColor : latestDiff < 0 ? '#ef4444' : undefined;
    const latestSign = latestDiff >= 0 ? '+' : '';

    let rollingNode: React.ReactNode = null;
    let rollingColor: string | undefined;
    let rollingSubTone: 'up' | 'down' | 'neutral' = 'neutral';
    let rollingSubText = '';

    if (rolling3 != null) {
      rollingNode = `${rolling3.toFixed(1)}%`;
      if (prev3 != null) {
        const raDiff = rolling3 - prev3;
        const raSign = raDiff >= 0 ? '+' : '';
        const raDir = raDiff > 0 ? 'Up' : raDiff < 0 ? 'Down' : 'Flat';
        rollingSubText = `${raDir} from ${prev3.toFixed(1)}% (${raSign}${raDiff.toFixed(1)}pp)`;
        rollingSubTone = raDiff > 0 ? 'up' : raDiff < 0 ? 'down' : 'neutral';
        rollingColor = raDiff > 0 ? upColor : raDiff < 0 ? '#ef4444' : undefined;
      }
    } else {
      rollingNode = 'N/A';
      rollingSubText = 'Need 3+ months of data';
    }

    return (
      <KpiRow>
        <KpiCard>
          <KpiLabel>Potential Collection</KpiLabel>
          <KpiValue>{formatMoney(potential)}</KpiValue>
          <KpiSub $tone="neutral">{latestMonth} inventory: {formatMoney(latestInv)}</KpiSub>
          <KpiDesc>Estimated collectible amount based on your {avgRetRate.toFixed(1)}% avg retention rate applied to latest inventory</KpiDesc>
        </KpiCard>

        <KpiCard>
          <KpiLabel>Avg PM Inventory</KpiLabel>
          <KpiValue>{formatMoney(avgInv)}</KpiValue>
          <KpiSub $tone="neutral">Across {monthCount} months</KpiSub>
          <KpiDesc>Average monthly PM Inventory across all months in the table</KpiDesc>
        </KpiCard>

        <KpiCard>
          <KpiLabel>Latest Retention %</KpiLabel>
          <KpiValue $color={latestColor}>{latestRetPct.toFixed(1)}%</KpiValue>
          <KpiSub $tone={latestTone}>Avg: {avgRetRate.toFixed(1)}% ({latestSign}{latestDiff.toFixed(1)}pp)</KpiSub>
          <KpiDesc>{latestMonth} retention rate compared to {avgRetRate.toFixed(1)}% historical average</KpiDesc>
        </KpiCard>

        <KpiCard>
          <KpiLabel>3-Month Retention Avg</KpiLabel>
          <KpiValue $color={rollingColor}>{rollingNode}</KpiValue>
          <KpiSub $tone={rollingSubTone}>{rollingSubText}</KpiSub>
          <KpiDesc>Smoothed retention trend — less noisy than month-to-month comparison</KpiDesc>
        </KpiCard>
      </KpiRow>
    );
  };

  return (
    <Container>
      <FrozenTop>
        {loading ? (
          <MomKpiSkeleton />
        ) : (
          rows.length > 0 && renderKpis()
        )}
        <Toolbar>
          <PageTitle>PM Inventory Analysis</PageTitle>
          <RecordCount>{loadFailed && rows.length === 0 ? 'Not loaded' : `${rows.length.toLocaleString()} months`}</RecordCount>
        </Toolbar>
      </FrozenTop>
      <TableScroll>
        {loading ? (
          <TableSkeleton columns={columns} />
        ) : loadFailed && rows.length === 0 ? (
          <LoadFailed what="month-on-month figures" onRetry={reload} />
        ) : rows.length === 0 ? (
          <EmptyState title="No month-on-month figures yet" hint="Months appear here once their end-of-month figures exist." />
        ) : (
          <DataTable
            parentScroll
            columns={columns}
            data={sorted}
            sort={sort}
            onSort={handleSort}
            // [MOBILE 2026-07-07] 13-column reconciliation matrix — keep the SAME
            // desktop table on phones (no card view); it scrolls horizontally inside
            // TableScroll so the full grid reads exactly like the website.
            disableMobileCards
          />
        )}
      </TableScroll>
    </Container>
  );
};

export default MonthOnMonthPage;
